import posthog, { type CaptureResult, type Properties, type SessionRecordingOptions } from 'posthog-js'

type AnalyticsApp = 'dashboard'

export interface SafeAnalyticsProperties {
  app?: AnalyticsApp
  environment?: string
  source?: string
  method?: string
  tour_uuid?: string
  scene_uuid?: string
  job_uuid?: string
  organization_uuid?: string
  plan_name?: string
  feature?: string
  quality?: string
  model?: string
  status?: string
  vote?: string
  reasons?: string
  // tour videos
  video_uuid?: string
  template?: string
  aspect?: string
  resolution?: string
  /** generation tier: fast | standard | premium (which Seedance model ran the job) */
  tier?: string
  duration_s?: string | number
  photo_count?: string | number
  audio?: string
  platform?: string
  video_source?: string
  entry_point?: string
  credits?: string | number
  // sales funnel
  interval?: string
  required_plan?: string
}

export interface DashboardAnalyticsEventMap {
  registration_submitted: SafeAnalyticsProperties
  registration_completed: SafeAnalyticsProperties
  login_completed: SafeAnalyticsProperties
  oauth_login_completed: SafeAnalyticsProperties
  logout: SafeAnalyticsProperties
  dashboard_page_viewed: SafeAnalyticsProperties
  dashboard_create_clicked: SafeAnalyticsProperties
  dashboard_film_opened: SafeAnalyticsProperties
  tour_created: SafeAnalyticsProperties
  full_tour_created: SafeAnalyticsProperties
  import_tour_created: SafeAnalyticsProperties
  builder_launch_clicked: SafeAnalyticsProperties
  ai_result_voted: SafeAnalyticsProperties
  tour_published: SafeAnalyticsProperties
  tour_qr_downloaded: SafeAnalyticsProperties
  billing_cta_clicked: SafeAnalyticsProperties
  upgrade_cta_clicked: SafeAnalyticsProperties
  checkout_returned: SafeAnalyticsProperties
  paywall_viewed: SafeAnalyticsProperties
  video_modal_opened: SafeAnalyticsProperties
  video_template_selected: SafeAnalyticsProperties
  video_capture_created: SafeAnalyticsProperties
  video_generate_started: SafeAnalyticsProperties
  video_generate_completed: SafeAnalyticsProperties
  video_generate_failed: SafeAnalyticsProperties
  video_generate_cancelled: SafeAnalyticsProperties
  video_audio_uploaded: SafeAnalyticsProperties
  video_soundtrack_added: SafeAnalyticsProperties
  video_export_started: SafeAnalyticsProperties
  video_shared: SafeAnalyticsProperties
  video_downloaded: SafeAnalyticsProperties
  video_deleted: SafeAnalyticsProperties
  video_embed_copied: SafeAnalyticsProperties
}

type AnalyticsUser = {
  id?: string | null
  uuid?: string | null
  email?: string | null
  role?: string | null
  type?: string | number | null
}

type AnalyticsOrganization = {
  uuid?: string | null
  organization_uuid?: string | null
  plan_name?: string | null
}

const SAFE_PROPERTY_KEYS = new Set<keyof SafeAnalyticsProperties>([
  'app',
  'environment',
  'source',
  'method',
  'tour_uuid',
  'scene_uuid',
  'job_uuid',
  'organization_uuid',
  'plan_name',
  'feature',
  'quality',
  'model',
  'status',
  'vote',
  'reasons',
  'video_uuid',
  'template',
  'aspect',
  'resolution',
  'duration_s',
  'photo_count',
  'audio',
  'platform',
  'video_source',
  'entry_point',
  'credits',
  'interval',
  'required_plan',
])

const SENSITIVE_KEY_PARTS = [
  'token',
  'password',
  'refreshtoken',
  'accesstoken',
  'authorization',
  'secret',
]

const URL_PROPERTY_KEYS = [
  '$current_url',
  '$referrer',
  '$initial_current_url',
  '$initial_referrer',
]

let initialized = false
let appName: AnalyticsApp = 'dashboard'

function isEnabled() {
  return import.meta.env.VITE_POSTHOG_ENABLED === 'true' && !!import.meta.env.VITE_POSTHOG_KEY
}

function environment() {
  return import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE || 'development'
}

function replaySampleRate() {
  const parsed = Number(import.meta.env.VITE_POSTHOG_REPLAY_SAMPLE_RATE)
  if (!Number.isFinite(parsed)) return 0.05
  return Math.min(Math.max(parsed, 0), 1)
}

function sessionRecordingOptions(): SessionRecordingOptions {
  return {
    sampleRate: replaySampleRate(),
    maskAllInputs: true,
    maskInputOptions: {
      color: true,
      date: true,
      'datetime-local': true,
      email: true,
      month: true,
      number: true,
      range: true,
      search: true,
      tel: true,
      text: true,
      time: true,
      url: true,
      week: true,
      textarea: true,
      select: true,
      password: true,
    },
    maskTextSelector: [
      '[contenteditable="true"]',
      '.ProseMirror',
      '.tiptap',
      '.ph-mask',
      '[data-sensitive]',
      '[data-private]',
      '[data-user-content]',
    ].join(', '),
    blockSelector: '.ph-no-capture, [data-posthog-block]',
    collectFonts: false,
    recordCrossOriginIframes: false,
    recordHeaders: false,
    recordBody: false,
    captureCanvas: { recordCanvas: false },
    maskCapturedNetworkRequestFn: () => null,
  }
}

function normalizeKey(key: string) {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '')
}

function sanitizeUrlProperty(value: unknown): unknown {
  if (typeof value !== 'string') return value

  try {
    const url = new URL(value, window.location.origin)
    url.search = ''
    url.hash = ''
    return url.toString()
  } catch {
    return value.split('?')[0]?.split('#')[0] || value
  }
}

function sanitizePostHogProperties(properties: Properties | undefined) {
  if (!properties) return
  const mutableProperties = properties as Record<string, unknown>

  for (const key of URL_PROPERTY_KEYS) {
    if (key in mutableProperties) {
      mutableProperties[key] = sanitizeUrlProperty(mutableProperties[key])
    }
  }
}

function hasSensitiveTopLevelKey(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  return Object.keys(value as Record<string, unknown>).some(key => {
    if (key.startsWith('$')) return false
    const normalized = normalizeKey(key)
    return SENSITIVE_KEY_PARTS.some(part => normalized.includes(part))
  })
}

function beforeSend(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event

  sanitizePostHogProperties(event.properties)

  if (
    hasSensitiveTopLevelKey(event.properties) ||
    hasSensitiveTopLevelKey(event.$set) ||
    hasSensitiveTopLevelKey(event.$set_once)
  ) {
    if (import.meta.env.DEV) {
      console.warn('[analytics] dropped event due to sensitive top-level key', event.event)
    }
    return null
  }
  return event
}

function cleanProperties(properties: SafeAnalyticsProperties = {}): Properties {
  const cleaned: Properties = {
    app: appName,
    environment: environment(),
  }

  for (const [key, value] of Object.entries(properties)) {
    if (!SAFE_PROPERTY_KEYS.has(key as keyof SafeAnalyticsProperties)) continue
    if (value === undefined || value === null || value === '') continue
    cleaned[key] = String(value)
  }

  return cleaned
}

function emailDomain(email?: string | null) {
  const domain = email?.split('@')[1]?.trim().toLowerCase()
  return domain || undefined
}

function roleForUser(user: AnalyticsUser) {
  if (user.role) return user.role
  if (user.type === undefined || user.type === null) return undefined
  return String(user.type)
}

export function initAnalytics(name: AnalyticsApp) {
  appName = name
  if (initialized || !isEnabled()) return

  const replayEnabled = import.meta.env.VITE_POSTHOG_REPLAY_ENABLED === 'true'

  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://ph.virtualtoureasy.com',
    ui_host: 'https://us.posthog.com',
    defaults: '2026-01-30',
    capture_pageview: 'history_change',
    capture_pageleave: true,
    autocapture: false,
    capture_performance: false,
    disable_session_recording: !replayEnabled,
    session_recording: sessionRecordingOptions(),
    disable_surveys: true,
    enable_recording_console_log: false,
    person_profiles: 'identified_only',
    before_send: beforeSend,
  })

  initialized = true
}

export function identifyUser(user: AnalyticsUser | null | undefined) {
  if (!initialized || !user) return
  const id = user.id || user.uuid
  if (!id) return

  posthog.identify(id, {
    email_domain: emailDomain(user.email),
    role: roleForUser(user),
    app: appName,
  })
}

export function groupOrganization(org: AnalyticsOrganization | null | undefined) {
  if (!initialized || !org) return
  const uuid = org.uuid || org.organization_uuid
  if (!uuid) return

  posthog.group('organization', uuid, {
    plan_name: org.plan_name || undefined,
  })
}

export function track<EventName extends keyof DashboardAnalyticsEventMap>(
  event: EventName,
  properties?: DashboardAnalyticsEventMap[EventName]
) {
  if (!initialized) return
  posthog.capture(event, cleanProperties(properties))
}

export function resetAnalytics() {
  if (!initialized) return
  posthog.reset()
}
